import React from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Divider,
  Fade,
  Zoom,
  alpha,
} from "@mui/material";
import lawgptLogo from "../assets/images/lawgptLogo.png";
import {
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  SupportAgent as SupportIcon,
} from "@mui/icons-material";
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    const {
      logo = "logo",
      primaryColor = "#6366f1",
      secondaryColor = "#f43f5e",
      title = "Oops! Something went wrong",
      subtitle = "We apologize for the inconvenience. Please try reloading the page or contact support if the issue persists.",
      contactEmail = "support@lawgpt.pk",
    } = this.props;

    if (this.state.hasError) {
      return (
        <Container
          //   maxWidth="sm"
          sx={{
            py: 6,
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Zoom in={true} timeout={500}>
            <Paper
              elevation={4}
              sx={{
                p: 5,
                boxShadow: "none",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Logo Section */}
                <Fade in={true} timeout={800}>
                  <Box
                    sx={{ mb: 2, display: "flex", justifyContent: "center" }}
                  >
                    {typeof logo === "string" ? (
                      <Box
                        component="img"
                        src={lawgptLogo}
                        alt="Application Logo"
                        sx={{
                          height: 80,
                          maxWidth: "100%",
                          objectFit: "contain",
                          filter: "drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.1))",
                        }}
                      />
                    ) : logo ? (
                      logo
                    ) : (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                          boxShadow: `0 8px 24px ${alpha(primaryColor, 0.25)}`,
                        }}
                      >
                        <Typography
                          variant="h4"
                          color="white"
                          fontWeight="bold"
                        >
                          App
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Fade>

                <Fade in={true} timeout={1000}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ErrorIcon
                      sx={{
                        fontSize: 40,
                        color: secondaryColor,
                        filter: `drop-shadow(0 2px 5px ${alpha(
                          secondaryColor,
                          0.4
                        )})`,
                      }}
                    />
                    <Typography
                      variant="h4"
                      component="h2"
                      fontWeight="bold"
                      sx={{
                        background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                        backgroundClip: "text",
                        textFillColor: "transparent",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </Fade>

                <Fade in={true} timeout={1200}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ maxWidth: "90%", mx: "auto", lineHeight: 1.6 }}
                  >
                    {subtitle}
                  </Typography>
                </Fade>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Fade in={true} timeout={1400}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<RefreshIcon />}
                      onClick={() => window.location.reload()}
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: "bold",
                        backgroundColor: primaryColor,
                        "&:hover": {
                          backgroundColor: alpha(primaryColor, 0.9),
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 16px ${alpha(primaryColor, 0.3)}`,
                        },
                        transition: "all 0.2s ease-in-out",
                        boxShadow: `0 4px 14px ${alpha(primaryColor, 0.4)}`,
                      }}
                    >
                      Reload Page
                    </Button>
                  </Fade>

                  <Fade in={true} timeout={1600}>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<SupportIcon />}
                      href={`mailto:${contactEmail}`}
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: "medium",
                        borderColor: alpha(primaryColor, 0.5),
                        color: primaryColor,
                        "&:hover": {
                          borderColor: primaryColor,
                          backgroundColor: alpha(primaryColor, 0.05),
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Contact Support
                    </Button>
                  </Fade>
                </Box>

                <Divider sx={{ width: "100%", my: 1, opacity: 0.6 }} />

                <Fade in={true} timeout={1800}>
                  <Accordion
                    sx={{
                      width: "100%",

                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                      backgroundColor: "transparent",
                      "& .MuiAccordionSummary-root": {
                        minHeight: "48px",
                        "&.Mui-expanded": {
                          minHeight: "48px",
                        },
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                        "&.Mui-expanded": {
                          margin: "12px 0",
                        },
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{ color: alpha(primaryColor, 0.7) }}
                        />
                      }
                      sx={{
                        px: 1,
                        "&:hover": {
                          backgroundColor: alpha(primaryColor, 0.03),
                          borderRadius: 2,
                        },
                      }}
                    >
                      <Typography
                        color="text.secondary"
                        fontWeight="medium"
                        fontSize="0.9rem"
                      >
                        Technical Details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1 }}>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: alpha("#000", 0.03),
                          borderRadius: 2,
                          fontFamily: '"Roboto Mono", monospace',
                          fontSize: "0.8rem",
                          overflow: "auto",
                          maxHeight: "200px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            whiteSpace: "pre-wrap",
                            color: alpha("#000", 0.7),
                          }}
                        >
                          {this.state.error && this.state.error.toString()}
                          {this.state.errorInfo && (
                            <>
                              <br />
                              <br />
                              {this.state.errorInfo.componentStack}
                            </>
                          )}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Fade>
              </Box>
            </Paper>
          </Zoom>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Wrapper to reset ErrorBoundary on route change
export const ErrorBoundaryWithKey = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.key}>{children}</ErrorBoundary>;
};

export default ErrorBoundary;
